import * as Flux from '../../lib/Flux.tsx';
import { Response, StatusCode } from '../../lib/HttpResponse.ts';
import { isDefined, Maybe } from '../../lib/MaybeV2.ts';
import * as Sentry from '@sentry/react';

export enum Error {
    NOT_PROVIDED,
    VIGNETTE_EXISTS,
}

export interface State<D> {
    pending: boolean;
    statusCode: StatusCode;
    errorMessage: Maybe<Error>;
    successData: Maybe<D>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errorData: Maybe<any>;
    attempts: number;
}

function captureError<D>(_s: State<D>, res: Response): void {
    if (!res.statusCode.cls.error) {
        return;
    }
    if (res.statusCode.timeout) {
        Sentry.captureMessage('Request Timeout');
        return;
    }
    const payload = res.request.payload;
    Sentry.withScope(scope => {
        scope.setContext('request data', {
            value: {
                payload: JSON.stringify(payload, null, 2),
                body: JSON.stringify(res.body, null, 2),
            },
        });
        const requestUrl = res.request.url;
        Sentry.captureMessage(`${requestUrl} ${res.statusCode.statusCode}`);
    });
}

export abstract class StateSlice<D> extends Flux.StateSlice<State<D>> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    abstract parseBody(body: any): D;

    sideEffects(): void {
        return;
    }

    getInitialState(): State<D> {
        return {
            pending: false,
            statusCode: new StatusCode(null),
            errorMessage: null,
            successData: null,
            errorData: null,
            attempts: 0,
        };
    }

    writeResponse(res: Response): void {
        this.set((s: State<D>): State<D> => {
            if (res.pending) {
                s.pending = true;
            } else {
                s.pending = false;
                s.statusCode = res.statusCode;
                s.attempts += 1;

                if (res.statusCode.cls.success) {
                    s.successData = this.parseBody(res.body);
                } else if (res.statusCode.cls.error) {
                    if (isDefined(res.body)) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const x: Error = Error[res.body.message] as any;
                        s.errorMessage = x;
                        s.errorData = res.body;
                    }
                    if (
                        !s.statusCode.unauthorized &&
                        s.statusCode.forbidden &&
                        s.statusCode.noResponse
                    ) {
                        captureError(s, res);
                    }
                }
            }
            return s;
        });
    }
}

interface GeneratorFunctions<D> {
    get: (store: Flux.Store) => State<D | null>;
    reset: (store: Flux.Store) => string;
    setResponse: (store: Flux.Store, res: Response) => void;
}

export const generate = <D>(
    key: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parseBody: (body: any) => D,
): GeneratorFunctions<D> => {
    class GenServerStateSlice extends StateSlice<D | null> {
        key(): string {
            return key;
        }

        getEmptyData(): Maybe<D> {
            return null;
        }

        sideEffects(_store?: Flux.Store): void {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        parseBody(body: any): D | null {
            if (body) {
                return parseBody(body);
            } else {
                return null;
            }
        }
    }

    return {
        get: (store: Flux.Store): State<D> =>
            new GenServerStateSlice(store).state as State<D>,
        reset: (store: Flux.Store): string => {
            new GenServerStateSlice(store).reset();
            return 'key-reset';
        },
        setResponse: (store: Flux.Store, res: Response): void =>
            new GenServerStateSlice(store).writeResponse(res),
    };
};
